import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Checkbox,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";
import { useWorkshopBlocker, FLOW } from "../../logic/useWorkshopBlocker";


type Option = {
  key: string;
  label_en: string;
  label_de: string;
  correct: boolean;
};

const OPTIONS: Option[] = [
  { key: "more_news", label_en: "Consume more news", label_de: "Mehr Nachrichten konsumieren", correct: false },
  { key: "stay_calm", label_en: "Stay calm & filter information", label_de: "Ruhe bewahren & Informationen filtern", correct: true },
  { key: "talk_others", label_en: "Talk to others", label_de: "Mit anderen sprechen", correct: true },
  { key: "spread_panic", label_en: "Spread panic", label_de: "Panik verbreiten", correct: false },
  { key: "routine", label_en: "Build a clear routine", label_de: "Klare Routine aufbauen", correct: true },
];

const STORAGE_PREFIX = "infoFilteringScenario";
const CACHE_TTL = 1000 * 60 * 30;

export default function ScenarioInformationOverload(props: { handlePageChange: (p: string) => void }) {
  const { handlePageChange } = props;
  const blocker = useWorkshopBlocker();
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const [selected, setSelected] = React.useState<string[]>([]);
  const [mode, setMode] = React.useState<"select" | "result">("select");
  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  /* ---------------- BLOCKER ---------------- */

  const myIndex = FLOW.indexOf("ScenarioInformationOverload");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;
  
  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  /* ---------------- INIT ---------------- */

  const loadLocal = React.useCallback(
    (workshopId: string, employeeId: string, backendSelected: string[]) => {
      const key = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;
      const saved = localStorage.getItem(key);

      if (backendSelected.length === 0) {
        localStorage.removeItem(key);
        setSelected([]);
        setMode("select");
        return;
      }

      if (!saved) {
        setSelected(backendSelected);
        setMode("result");
        return;
      }

      try {
        const parsed = JSON.parse(saved);

        if (
          !parsed ||
          !Array.isArray(parsed.selected) ||
          typeof parsed.timestamp !== "number"
        ) {
          localStorage.removeItem(key);
          setSelected(backendSelected);
          setMode("result");
          return;
        }

        const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;

        if (isExpired) {
          localStorage.removeItem(key);
          setSelected(backendSelected);
          setMode("result");
          return;
        }

        setSelected(parsed.selected);
        setMode("result");
      } catch {
        localStorage.removeItem(key);
        setSelected(backendSelected);
        setMode("result");
      }
    },
    []
  );

  const loadContext = React.useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) return;

    setEmployeeId(emp.id);
    setWorkshopId(workshop.id);

    const { data: existingAnswers, error } = await supabase
      .from("workshop_participant_information_filtering")
      .select("option_key")
      .eq("workshop_id", workshop.id)
      .eq("employee_id", emp.id);

    if (error) {
      console.error("LOAD INFORMATION FILTERING ERROR:", error);
      return;
    }

    const backendSelected = existingAnswers?.map((row) => row.option_key) ?? [];

    loadLocal(workshop.id, emp.id, backendSelected);
  }, [loadLocal]);
  
  React.useEffect(() => {
    loadContext();
  }, [loadContext]);

  /* ---------------- TOGGLE ---------------- */

  function toggle(key: string) {
    if (mode === "result") return;

    setSelected((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  }

  /* ---------------- SUBMIT ---------------- */

  async function handleSubmit() {
    if (!workshopId || !employeeId) return;

    const storageKey = `${STORAGE_PREFIX}_${workshopId}_${employeeId}`;

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        selected,
        timestamp: Date.now(),
      })
    );

    await supabase
      .from("workshop_participant_information_filtering")
      .delete()
      .eq("workshop_id", workshopId)
      .eq("employee_id", employeeId);

    const inserts = selected.map((key) => {
      const opt = OPTIONS.find((o) => o.key === key);
      return {
        workshop_id: workshopId,
        employee_id: employeeId,
        option_key: key,
        is_correct: opt?.correct ?? false,
      };
    });

    await supabase
      .from("workshop_participant_information_filtering")
      .insert(inserts);

    setMode("result");
  }

  /* ---------------- HELPERS ---------------- */

  function getColor(opt: Option) {
    if (mode === "select") return "rgba(255,255,255,0.2)";

    const isSelected = selected.includes(opt.key);

    if (isSelected && opt.correct) return "#4caf50";
    if (isSelected && !opt.correct) return "#ff5252";
    if (!isSelected && opt.correct) return "#ffc107";

    return "rgba(255,255,255,0.1)";
  }

  function getIndicator(opt: Option) {
    if (mode === "select") return "";

    const isSelected = selected.includes(opt.key);

    if (isSelected && opt.correct) return "✔";
    if (isSelected && !opt.correct) return "✖";
    if (!isSelected && opt.correct) return "!";

    return "";
  }

  function handleRedo() {
    if (workshopId && employeeId) {
      localStorage.removeItem(`${STORAGE_PREFIX}_${workshopId}_${employeeId}`);
    }

    setSelected([]);
    setMode("select");
  }

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/infoOverload.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        py: { xs: 3, sm: 5 },
      }}
    >
      <Paper
        sx={{
          width: "min(900px, 100%)",
          maxWidth: 900,
          p: { xs: 2.5, sm: 4, md: 5 },
          borderRadius: { xs: 3, sm: 5 },
          background: "linear-gradient(180deg, #311f1f, #221515)",
        }}
      >
        <Stack spacing={{ xs: 3, sm: 4 }}>

          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.6rem", md: "1.8rem" } }}>
            {isDE
              ? "Du erhältst widersprüchliche Informationen"
              : "You receive conflicting information"}
          </Typography>

          <Typography sx={{ color: "#aaa", whiteSpace: "pre-line" }}>
            {isDE
              ? `• widersprüchliche Nachrichten
                  • Social Media Gerüchte
                  • keine klare offizielle Information

                  👉 Was stimmt?
                  👉 Was soll ich tun?`
              : `• conflicting messages
                  • social media rumors
                  • no clear official information

                  👉 What is true?
                  👉 What should I do?`}
          </Typography>

          <Typography sx={{ color: "#ff8a8a", fontWeight: 700 }}>
            {isDE ? "Was hilft dir jetzt wirklich?" : "What actually helps you now?"}
          </Typography>

          {/* OPTIONS */}
          <Stack>
            {OPTIONS.map((opt) => {
              const checked = selected.includes(opt.key);

              return (
                <Box
                  key={opt.key}
                  sx={{
                    border: `1px solid ${getColor(opt)}`,
                    borderRadius: 2,
                    mb: 1,
                    px: 2,
                    py: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Box
                    onClick={() => toggle(opt.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: mode === "result" ? "default" : "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={mode === "result"}
                      sx={{
                        color: "#888",
                        "&.Mui-checked": { color: "#ff4d4d" },
                      }}
                    />

                    <Typography sx={{ color: "#fff" }}>
                      {isDE ? opt.label_de : opt.label_en}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontWeight: 800, color: getColor(opt) }}>
                    {getIndicator(opt)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          {mode === "select" ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={selected.length === 0}
              sx={{
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                fontWeight: 700,
                "&.Mui-disabled": {
                  background: "#777",
                  color: "rgba(255,255,255,0.75)",
                },
              }}
            >
              {isDE ? "Antwort speichern" : "Submit answer"}
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleRedo}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                fontWeight: 700,
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {isDE ? "Antwort ändern" : "Change answer"}
            </Button>
          )}

          {mode === "result" && (
            <Button
              variant="contained"
              onClick={handleContinue}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                "&:hover": {
                  bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                },
              }}
            >
              {isDE ? "Weiter" : "Continue"}
            </Button>
          )}
          
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab." : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}